/**
 * useGlobalSearch — debounced data-zoek voor de Cmd+K palette.
 *
 * Roept /api/search/global aan (300ms debounce, min 2 tekens, abort bij
 * nieuwe input) en geeft gegroepeerde resultaten terug. Zelfde fetch-patroon
 * als CustomerLinker.
 */

import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

export type GlobalResultType =
  | 'customer'
  | 'contact'
  | 'offerte'
  | 'order'
  | 'invoice'
  | 'opportunity'
  | 'item';

export interface GlobalSearchResult {
  id: string;
  type: GlobalResultType;
  title: string;
  subtitle: string;
  meta: string;
  badge?: 'blacklist';
  /** Alleen bij type='contact': klantpagina + contact-drawer openen. */
  accountId?: string;
}

export interface GlobalSearchGroups {
  customers: GlobalSearchResult[];
  contacts: GlobalSearchResult[];
  offertes: GlobalSearchResult[];
  orders: GlobalSearchResult[];
  invoices: GlobalSearchResult[];
  opportunities: GlobalSearchResult[];
  items: GlobalSearchResult[];
}

export const EMPTY_GROUPS: GlobalSearchGroups = {
  customers: [],
  contacts: [],
  offertes: [],
  orders: [],
  invoices: [],
  opportunities: [],
  items: [],
};

export function useGlobalSearch(query: string, enabled: boolean) {
  const [groups, setGroups] = useState<GlobalSearchGroups>(EMPTY_GROUPS);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length < 2) {
      abortRef.current?.abort();
      setGroups(EMPTY_GROUPS);
      setIsLoading(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const res = await fetch(`/api/search/global?q=${encodeURIComponent(q)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok) {
          setGroups(EMPTY_GROUPS);
          return;
        }
        const data = (await res.json()) as { groups?: Partial<GlobalSearchGroups> };
        setGroups({ ...EMPTY_GROUPS, ...(data.groups ?? {}) });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('[useGlobalSearch] error:', err);
        }
      } finally {
        if (abortRef.current === controller) setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, enabled]);

  // Abort lopende fetch bij unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { groups, isLoading };
}
